// PAB Main Product — gallery, variant chips, upsell, add-to-cart, sticky bar.
// Vanilla web component; integrates with Horizon's cart drawer via CartAddEvent.
import { CartAddEvent, CartErrorEvent } from '@theme/events';

class PabProduct extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('form[data-type="add-to-cart-form"], form[action*="/cart/add"]');
    this.variants = JSON.parse(this.querySelector('[data-pab-variants]')?.textContent || '[]');
    this.currency = this.dataset.currency || 'USD';
    this.fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: this.currency,
    });

    this.#initGallery();
    this.#initOptions();
    this.#initUpsell();
    this.#initAtc();
    this.#initAccordions();
    this.#initStickyBar();
    this.#updateState();
  }

  /* ---------------- gallery ---------------- */
  #initGallery() {
    const scroller = this.querySelector('[data-pab-carousel]');
    if (!scroller) return;
    const slides = [...scroller.children];
    const dotsWrap = this.querySelector('[data-pab-dots]');
    const thumbs = [...this.querySelectorAll('[data-pab-thumbs] button')];

    if (dotsWrap) {
      dotsWrap.innerHTML = slides.map((_, i) => `<span${i === 0 ? ' class="on"' : ''}></span>`).join('');
    }
    const dots = dotsWrap ? [...dotsWrap.children] : [];

    let active = 0;
    const setActive = (i) => {
      if (i === active) return;
      active = i;
      dots.forEach((d, j) => d.classList.toggle('on', j === i));
      thumbs.forEach((t, j) => t.classList.toggle('on', j === i));
    };

    let raf = null;
    scroller.addEventListener(
      'scroll',
      () => {
        if (raf) return;
        raf = requestAnimationFrame(() => {
          raf = null;
          let best = 0;
          let bd = Infinity;
          slides.forEach((s, i) => {
            const d = Math.abs(s.offsetLeft - scroller.scrollLeft);
            if (d < bd) { bd = d; best = i; }
          });
          setActive(best);
        });
      },
      { passive: true }
    );

    thumbs.forEach((t) => {
      t.addEventListener('click', () => {
        const i = Number(t.dataset.index) || 0;
        scroller.scrollLeft = slides[i]?.offsetLeft ?? 0;
        setActive(i);
      });
    });
  }

  /* ---------------- variant options ---------------- */
  #initOptions() {
    this.optionGroups = [...this.querySelectorAll('[data-pab-option]')];
    this.optionGroups.forEach((group) => {
      group.addEventListener('click', (e) => {
        const chip = e.target.closest('.pab-chip');
        if (!chip) return;
        group.querySelectorAll('.pab-chip').forEach((c) => {
          c.classList.toggle('on', c === chip);
          c.setAttribute('aria-pressed', c === chip ? 'true' : 'false');
        });
        this.#updateState();
      });
    });
  }

  get #selectedOptions() {
    return this.optionGroups.map((g) => g.querySelector('.pab-chip.on')?.dataset.value?.trim() ?? null);
  }

  get #currentVariant() {
    if (!this.variants.length) return null;
    if (!this.optionGroups.length) return this.variants[0];
    const sel = this.#selectedOptions;
    return this.variants.find((v) => v.options.every((o, i) => String(o).trim() === sel[i])) || null;
  }

  /* ---------------- upsells (one per upsell block) ---------------- */
  #initUpsell() {
    this.upsells = [...this.querySelectorAll('[data-pab-upsell]')];
    this.upsells.forEach((upsell) => {
      const toggle = upsell.querySelector('[data-pab-upsell-toggle]');
      toggle?.addEventListener('click', () => {
        const on = !upsell.classList.contains('on');
        upsell.classList.toggle('on', on);
        toggle.classList.toggle('on', on);
        toggle.setAttribute('aria-checked', String(on));
        this.#updateState();
      });
    });
  }

  get #activeUpsells() {
    return this.upsells.filter((u) => u.classList.contains('on'));
  }

  /* ---------------- price / ATC state ---------------- */
  #updateState() {
    const v = this.#currentVariant;
    const price = this.querySelector('[data-pab-price]');
    const atc = this.querySelector('[data-pab-atc]');
    const label = this.querySelector('[data-pab-atc-label]');
    const total = this.querySelector('[data-pab-atc-total]');
    const idInput = this.querySelector('[data-pab-variant-id]');
    const atcText = atc?.dataset.label || label?.dataset.default || null;

    if (v && price) {
      price.querySelector('.now').textContent = v.price_f;
      const was = price.querySelector('.was');
      const save = price.querySelector('.save');
      if (v.compare > v.price) {
        was.hidden = false;
        save.hidden = false;
        was.textContent = v.compare_f;
        save.textContent = `Save ${Math.round((1 - v.price / v.compare) * 100)}%`;
      } else {
        was.hidden = true;
        save.hidden = true;
      }
    }

    if (idInput && v) idInput.value = v.id;

    if (atc && label && total) {
      if (!label.dataset.default) label.dataset.default = label.textContent.trim();
      if (!v) {
        atc.disabled = true;
        label.textContent = 'Unavailable';
        total.textContent = '';
      } else if (!v.available) {
        atc.disabled = true;
        label.textContent = 'Sold out';
        total.textContent = '';
      } else {
        atc.disabled = false;
        label.textContent = label.dataset.default;
        const cents = this.#activeUpsells.reduce((sum, u) => sum + Number(u.dataset.price || 0), v.price);
        total.textContent = `· ${this.fmt.format(cents / 100)}`;
      }
    }
  }

  /* ---------------- add to cart ---------------- */
  #initAtc() {
    this.form?.addEventListener('submit', (e) => {
      e.preventDefault();
      this.#addToCart();
    });
  }

  async #addToCart() {
    const v = this.#currentVariant;
    if (!v || !v.available) return;
    const atc = this.querySelector('[data-pab-atc]');
    atc.disabled = true;

    const items = [{ id: v.id, quantity: 1 }];
    this.#activeUpsells.forEach((u) => {
      if (u.dataset.variantId) items.push({ id: Number(u.dataset.variantId), quantity: 1 });
    });

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((el) => el.dataset.sectionId)
      .filter(Boolean);

    try {
      const res = await fetch(window.Theme?.routes?.cart_add_url || '/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await res.json();

      if (data.status) {
        this.dispatchEvent(new CartErrorEvent(this.form?.id || '', data.message, data.description, data.errors));
        return;
      }

      this.dispatchEvent(
        new CartAddEvent({}, String(v.id), {
          source: 'product-form-component',
          itemCount: items.length,
          productId: this.dataset.productId,
          variantId: String(v.id),
          sections: data.sections,
        })
      );
    } catch (err) {
      console.error('[pab-product] add to cart failed', err);
    } finally {
      atc.disabled = false;
      this.#updateState();
    }
  }

  /* ---------------- info accordions ---------------- */
  #initAccordions() {
    const wrap = this.querySelector('[data-pab-accordions]');
    wrap?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-pab-acc-btn]');
      if (!btn) return;
      const item = btn.closest('.pab-iac');
      const open = !item.classList.contains('open');
      wrap.querySelectorAll('.pab-iac.open').forEach((i) => {
        i.classList.remove('open');
        i.querySelector('[data-pab-acc-btn]')?.setAttribute('aria-expanded', 'false');
      });
      item.classList.toggle('open', open);
      btn.setAttribute('aria-expanded', String(open));
    });
  }

  /* ---------------- sticky bottom bar ---------------- */
  #initStickyBar() {
    const bar = this.querySelector('[data-pab-sticky]');
    if (!bar) return;
    const buy = this.querySelector('.pab-buy');
    const foot = document.querySelector('footer, .shopify-section-group-footer-group');

    let pastBuy = false;
    let footVis = false;
    const apply = () => {
      const show = pastBuy && !footVis;
      bar.classList.toggle('on', show);
      bar.setAttribute('aria-hidden', String(!show));
    };

    if (buy) {
      new IntersectionObserver(
        ([e]) => {
          pastBuy = !e.isIntersecting && e.boundingClientRect.top < 0;
          apply();
        },
        { threshold: 0 }
      ).observe(buy);
    }
    if (foot) {
      new IntersectionObserver(
        ([e]) => {
          footVis = e.isIntersecting;
          apply();
        },
        { threshold: 0 }
      ).observe(foot);
    }

    bar.querySelector('[data-pab-sticky-cta]')?.addEventListener('click', () => {
      const el = this.querySelector('[data-pab-selzone]') || buy;
      if (!el) return;
      const header = parseFloat(getComputedStyle(document.body).getPropertyValue('--header-height')) || 68;
      window.scrollTo({
        top: el.getBoundingClientRect().top + window.scrollY - header - 32,
        behavior: 'smooth',
      });
    });
  }
}

if (!customElements.get('pab-product')) customElements.define('pab-product', PabProduct);
